import { getCalibrationScore } from 'libs/end-to-end/src/tests-utils';
import React, { useMemo, useState } from 'react';
import { LinkChevronSvg } from 'apps/sigma2-ui/src/components/svg/LinkChevron';
import { Test } from 'libs/end-to-end/src/tests-utils';
import { getTestCountsByFocusArea } from 'libs/end-to-end/src/tests-utils';
import { OverviewFocusAreaPolarChart } from 'apps/sigma2-ui/src/components/overview/OverviewFocusAreaPolarChart';
import { TestSummary } from 'libs/end-to-end/src/tests-utils';
import { RoutableLink } from 'apps/sigma2-ui/src/components/ui/RoutableLink';
import { IconBarChartSvg } from 'apps/sigma2-ui/src/components/svg/IconBarChart';
import { IconRadarChartSvg } from 'apps/sigma2-ui/src/components/svg/IconRadarChart';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { OverviewFocusAreaBarChart } from 'apps/sigma2-ui/src/components/overview/OverviewFocusAreaBarChart';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { useShouldShowRadarChart } from 'apps/sigma2-ui/src/lib/hooks/useShouldShowRadarChart';
import { getFocusAreaHref } from 'apps/sigma2-ui/src/lib/focusArea';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

/**
 * This component is the "For You" section.
 * @param testSummary
 * @param focusAreas
 * @param calibrationFraming
 * @constructor
 */
export const DashboardFocusAreaSummary = ({
  testSummary,
  focusAreas,
  calibrationFraming,
}: {
  testSummary: TestSummary | null;
  focusAreas: MyFocusAreaDefinition[] | null;
  calibrationFraming: CalibrationFraming;
}) => {
  const language = useLanguage();
  const [graphType, setGraphType] = useState<'radar' | 'bar'>('radar');

  const topAreaContributors = useMemo(() => {
    if (!testSummary || !focusAreas) {
      return null;
    }

    const contributors: {
      focusArea: MyFocusAreaDefinition;
      href: string;
    }[] = focusAreas.map((focusArea) => {
      return {
        focusArea: focusArea,
        href: getFocusAreaHref(focusArea.focusArea),
      };
    });

    const tests: Test[] = testSummary.tests ?? [];

    // Only return the top 3 contributors (aka worst performing) based on test summary optimization score
    return [...contributors]
      .sort((a, b) => {
        const aCounts = getTestCountsByFocusArea(tests, a.focusArea.focusArea);
        const bCounts = getTestCountsByFocusArea(tests, b.focusArea.focusArea);
        const aCalibrationScore = getCalibrationScore({
          testCount: aCounts,
        });
        const bCalibrationScore = getCalibrationScore({
          testCount: bCounts,
        });
        return aCalibrationScore - bCalibrationScore;
      })
      .slice(0, 3);
  }, [testSummary, focusAreas]);

  const shouldShowRadarChart = useShouldShowRadarChart(focusAreas?.length);

  return (
    <div className="font-sans mt-6 grid grid-cols-3 shadow-2px border-solid rounded-lg">
      <Spacing
        direction="horizontal"
        size=""
        className="bg-white items-center rounded-l-lg"
      >
        <Spacing
          direction="vertical"
          size="gap-[10px]"
          className="px-4 xl:px-10"
        >
          <S2Typography
            variant="headline-4"
            translatedContent={language.overviewVisualizationHeading({
              calibrationFraming,
            })}
          />
          <S2Typography
            variant="body-6"
            translatedContent={language.overviewVisualizationBlurb()}
            dangerouslyOverrideClassNameFn={replaceTextColor('text-gray-600')}
          />

          {topAreaContributors && topAreaContributors.length > 0 && (
            <>
              <S2Typography
                variant="headline-5-alt"
                translatedContent={language.overviewVisualizationContributors()}
                dangerouslyOverrideClassNameFn={replaceTextColor(
                  'text-gray-800',
                )}
              />
              {topAreaContributors.map((area, i) => (
                <RoutableLink
                  to={area.href}
                  className="text-primary font-medium text-sm xl:text-base flex flex-row items-center gap-[5px]"
                  key={i}
                >
                  <span>{area.focusArea.displayName}</span>
                  <LinkChevronSvg svg={{ className: 'w-4 h-4 block' }} />
                </RoutableLink>
              ))}
            </>
          )}
        </Spacing>
      </Spacing>
      <div className="col-span-2 bg-gray-100">
        <Spacing
          direction="horizontal"
          size="xs"
          className="w-full flex-row-reverse px-4 pt-4"
        >
          {shouldShowRadarChart && (
            <>
              <div
                className={cn('p-2 rounded', {
                  'bg-royal-200 text-royal-700': graphType === 'bar',
                })}
                onClick={() => {
                  setGraphType('bar');
                }}
              >
                <IconBarChartSvg svg={{ className: 'w-6 h-6' }} />
              </div>
              <div
                className={cn('p-2 rounded', {
                  'bg-royal-200 text-royal-700': graphType === 'radar',
                })}
                onClick={() => {
                  setGraphType('radar');
                }}
              >
                <IconRadarChartSvg svg={{ className: 'w-6 h-6' }} />
              </div>
            </>
          )}
        </Spacing>
        {graphType === 'radar' && shouldShowRadarChart ? (
          <OverviewFocusAreaPolarChart
            testSummary={testSummary ?? null}
            focusAreas={focusAreas ?? null}
            calibrationFraming={calibrationFraming}
          />
        ) : (
          <OverviewFocusAreaBarChart
            testSummary={testSummary ?? null}
            focusAreas={focusAreas ?? null}
            calibrationFraming={calibrationFraming}
          />
        )}
      </div>
    </div>
  );
};
